import { showSuccessMessage } from 'helpers';
import { groupBy } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setShowingsTabEffect } from 'store/effects/showingAggregate';
import {
  requestUpdateShowingAppointmentApprovalEffect,
  requestUpdateShowingAppointmentEffect,
} from 'store/effects/showingAppointment';

import { getUserId } from 'store/selectors/user';
import { AppointmentStatus, ApprovalAction, ApprovalStatus } from 'types';

export const useManageShowingsList = ({ fetchShowingAggregate, activeTab }) => {
  const params = useParams<any>();

  const [showModal, setShowModal] = useState(false);
  const [appointmentModal, setAppointmentModal] = useState(false);

  const [actionConfig, setActionConfig] = useState({
    action: '',
  });

  const [pendingApprovers, setPendingApprovers] = useState([]);

  const [isFeedback, setIsNewFeedback] = useState(false);
  const [isUserApproverForPendingApproval, setUserApproverForPendingApproval] = useState(false);

  const [data, setData] = useState({ UUID: 0 });
  const userId = useSelector(getUserId);

  const dispatch = useDispatch();

  useEffect(() => {
    if (params?.uuid) {
      setData({ UUID: params?.uuid });
      setAppointmentModal(true);
    }
  }, [params]);

  const processAction = (action) => {
    setShowModal(true);
    setActionConfig({ ...actionConfig, action });
  };

  const getPendingApproverNames = (data) => {
    let names: any = [];
    const approverGroupByStatus = groupBy(data.ShowingAppointmentApproval, 'ApprovalStatus');
    approverGroupByStatus?.Pending?.forEach((Approval) => {
      const { ApproverName, ApproverUserEmail, ApproverUserId } = Approval;
      const name =
        userId && userId === ApproverUserId
          ? 'You'
          : `${ApproverName?.FirstName} ${ApproverName?.LastName}`;
      names.push(ApproverName ? name : ApproverUserEmail);
    });

    return names;
  };

  const processData = (data) => {
    setData(data);
    const approvers = getPendingApproverNames(data);
    setIsNewFeedback(data?.IsNewFeedback);
    setPendingApprovers(approvers);
    setUserApproverForPendingApproval(approvers?.includes('You'));
  };

  const redirectToOverview = (record) => {
    processData(record);
    setAppointmentModal(true);
  };

  const checkApproverSequence = (data): boolean => {
    const approverGroupByStatus = groupBy(data?.ShowingAppointmentApproval, 'ApprovalStatus');
    const pendingApprovers = approverGroupByStatus?.Pending;

    if (pendingApprovers?.length && pendingApprovers?.[0]?.ApprovalSequence) {
      for (let i = 0; i < pendingApprovers?.length; i++) {
        const approver = pendingApprovers[i];
        const prevApprover = i ? pendingApprovers[i - 1] : null;

        if (
          approver?.ApproverUserId === userId &&
          prevApprover?.ApprovalStatus === ApprovalStatus.Pending
        ) {
          return false;
        }
      }
    }
    return true;
  };

  const isApprovalRequired = (data) => {
    return isUserApproverForPendingApproval && checkApproverSequence(data);
  };

  const validateApprover = (type, data) => {
    const { ShowingAppointmentApproval } = data;
    const approverGroupByStatus = groupBy(ShowingAppointmentApproval, 'ApprovalStatus');
    const approvers = approverGroupByStatus[type] || [];
    return approvers.find((a) => a.ApproverUserId === userId);
  };

  const onReasonSubmit = (approvalStatus, approverStatus) => {
    const approver = validateApprover(approverStatus, data);
    if (approver) {
      dispatch(
        requestUpdateShowingAppointmentApprovalEffect(
          { UUID: approver.UUID, ApprovalStatus: approvalStatus },
          {},
          (err) => {
            if (!err) {
              onSuccess(approvalStatus);
            }
          },
        ),
      );
    }
  };

  const onReasonCancel = () => {
    dispatch(
      requestUpdateShowingAppointmentEffect(
        { UUID: data && data?.UUID, AppointmentStatus: AppointmentStatus.Canceled },
        {},
        (err) => {
          if (!err) {
            onSuccess(AppointmentStatus.Canceled);
          }
        },
      ),
    );
  };

  const onReasonConfirm = () => {
    switch (actionConfig?.action) {
      case ApprovalAction.Approve:
        onReasonSubmit(ApprovalStatus.Approved, ApprovalStatus.Pending);
        break;
      case ApprovalAction.Decline:
        onReasonSubmit(ApprovalStatus.Rejected, ApprovalStatus.Pending);
        break;
      case ApprovalAction.Cancel:
        onReasonCancel();
        break;
      default:
        break;
    }
    setShowModal(false);
  };

  const onSuccess = (status) => {
    showSuccessMessage(`Showing ${status} Successfully`);
    dispatch(setShowingsTabEffect(activeTab));
    fetchShowingAggregate();
  };

  const onConfirmationCancel = (e) => {
    e.stopPropagation();
    setShowModal(false);
  };

  const onDetailsClose = () => {
    setAppointmentModal(false);
    setData({ UUID: 0 });
    if (isFeedback) {
      dispatch(setShowingsTabEffect(activeTab));
      fetchShowingAggregate();
    }
  };

  return {
    data,
    setData,
    showModal,
    isFeedback,
    processData,
    actionConfig,
    processAction,
    onReasonCancel,
    onDetailsClose,
    onReasonConfirm,
    pendingApprovers,
    appointmentModal,
    isApprovalRequired,
    redirectToOverview,
    onConfirmationCancel,
    getPendingApproverNames,
  };
};
